import { memo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  X,
  FileBarChart,
  ClipboardList,
  BookOpen,
  ChevronLeft,
  Menu,
  Bell,
  Settings,
  Sun,
  Moon,
  User,
  LogOut,
  HelpCircle,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTheme } from '@/app/providers/ThemeProvider';
import { UserProfileModal } from '@/components/modals/UserProfileModal';
import { SecuritySettingsModal } from '@/components/modals/SecuritySettingsModal';
import { HelpSupportModal } from '@/components/modals/HelpSupportModal';

// Menu items moved outside component to prevent recreation on every render
const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'doctors', label: 'Médicos', icon: Users, path: '/doctors' },
  { id: 'tasks', label: 'Trámites', icon: ClipboardList, path: '/tasks' },
  {
    id: 'billing-cuts',
    label: 'Cortes de Facturación',
    icon: FileBarChart,
    path: '/billing-cuts',
  },
  { id: 'directory', label: 'Directorio', icon: BookOpen, path: '/directory' },
];

interface SidebarProps {
  currentPage: string;
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onLogout?: () => void;
}

export const Sidebar = memo(function Sidebar({
  isOpen = true,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
  onLogout,
}: SidebarProps) {
  const { theme, mode, toggleTheme } = useTheme();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Determine theme label for display (based on mode, not theme)
  const getThemeLabel = () => {
    if (mode === 'dark') return 'Oscuro';
    if (mode === 'light') return 'Claro';
    return 'Auto';
  };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile overlay */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex h-screen transform flex-col border-r border-gray-200 bg-white transition-all duration-200 ease-in-out dark:border-gray-800 dark:bg-gray-900 lg:static ${
          isCollapsed ? 'w-16' : 'w-64'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Close button for mobile */}
        <div className="absolute right-2 top-3 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Header with logo and collapse toggle */}
        <div
          className={`flex flex-shrink-0 items-center border-b border-gray-200 dark:border-gray-800 ${isCollapsed ? 'justify-center p-3' : 'justify-between p-3'}`}
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
            <img src="/logo.svg" alt="byEGB" className="h-9 w-9 flex-shrink-0 rounded-lg" />
            {!isCollapsed && (
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">byEGB</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sistema médico</p>
              </div>
            )}
          </div>

          {/* Collapse toggle - hidden on mobile, visible on lg+ */}
          {!isCollapsed && onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="hidden h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 lg:flex"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {isCollapsed && onToggleCollapse && (
          <div className="hidden flex-shrink-0 border-b border-gray-200 p-2 dark:border-gray-800 lg:block">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleCollapse}
                  className="h-10 w-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Expandir menú</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Navigation - flex-1 */}
        <nav className={`flex-1 ${isCollapsed ? '' : 'overflow-y-auto'} p-2`}>
          <div className={isCollapsed ? 'space-y-1' : 'flex flex-col gap-1'}>
            {menuItems.map((item) => {
              const Icon = item.icon;

              if (isCollapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.path}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex h-10 w-full items-center justify-center rounded-lg transition-colors ${
                            isActive
                              ? 'bg-[var(--blue-3)] text-[var(--blue-11)] dark:bg-[var(--blue-4)] dark:text-[var(--blue-9)]'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                          }`
                        }
                        style={{ display: 'flex', width: '100%' }}
                      >
                        <Icon className="h-5 w-5" strokeWidth={2} />
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-[var(--blue-3)] font-medium text-[var(--blue-11)] dark:bg-[var(--blue-4)] dark:text-[var(--blue-9)]'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    }`
                  }
                >
                  <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Bottom section - utilities and user (always visible, never scrolls) */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800">
          {/* Theme toggle */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  className="flex h-11 w-full items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                >
                  {theme === 'dark' ? (
                    <Sun className="h-5 w-5" strokeWidth={2} />
                  ) : (
                    <Moon className="h-5 w-5" strokeWidth={2} />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Tema: {getThemeLabel()}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={toggleTheme}
              className="flex w-full items-center space-x-3 px-3 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
              ) : (
                <Moon className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
              )}
              <span>Tema: {getThemeLabel()}</span>
            </button>
          )}

          {/* Notifications */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="relative flex h-11 w-full items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100">
                  <Bell className="h-5 w-5" strokeWidth={2} />
                  <span className="absolute right-3 top-2.5 h-2 w-2 rounded-full bg-blue-600"></span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Notificaciones</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <button className="relative flex w-full items-center space-x-3 px-3 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100">
              <Bell className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
              <span>Notificaciones</span>
              <span className="ml-auto h-2 w-2 rounded-full bg-blue-600"></span>
            </button>
          )}

          {/* Settings */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="flex h-11 w-full items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100">
                  <Settings className="h-5 w-5" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Configuración</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <button className="flex w-full items-center space-x-3 px-3 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100">
              <Settings className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
              <span>Configuración</span>
            </button>
          )}

          {/* User section */}
          <div className="border-t border-gray-200 p-2 dark:border-gray-800">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={
                    isCollapsed
                      ? 'flex h-11 w-full items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800'
                      : 'flex w-full items-center space-x-3 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                >
                  <Avatar
                    className={`ring-2 ring-[var(--blue-3)] dark:ring-[var(--blue-4)] ${isCollapsed ? 'h-8 w-8' : 'h-8 w-8 flex-shrink-0'}`}
                  >
                    <AvatarFallback className="bg-gradient-to-br from-[var(--blue-9)] to-[var(--blue-10)] text-xs text-white">
                      AD
                    </AvatarFallback>
                  </Avatar>
                  {!isCollapsed && (
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        Administrador
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        admin@sistema.com
                      </p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={isCollapsed ? 'center' : 'end'}
                side={isCollapsed ? 'right' : 'top'}
                className="w-56 border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900"
              >
                <DropdownMenuLabel className="px-3 py-2">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-9 w-9 ring-2 ring-[var(--blue-3)] dark:ring-[var(--blue-4)]">
                      <AvatarFallback className="bg-gradient-to-br from-[var(--blue-9)] to-[var(--blue-10)] text-sm text-white">
                        AD
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Administrador
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        admin@sistema.com
                      </p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-800" />
                <DropdownMenuItem
                  className="cursor-pointer px-3 py-2 focus:bg-[var(--blue-2)] focus:text-[var(--blue-11)] dark:focus:bg-[var(--blue-3)] dark:focus:text-[var(--blue-9)]"
                  onClick={() => setShowProfileModal(true)}
                >
                  <User className="mr-3 h-4 w-4" />
                  <span className="text-sm">Mi Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer px-3 py-2 focus:bg-[var(--blue-2)] focus:text-[var(--blue-11)] dark:focus:bg-[var(--blue-3)] dark:focus:text-[var(--blue-9)]"
                  onClick={() => setShowSecurityModal(true)}
                >
                  <Shield className="mr-3 h-4 w-4" />
                  <span className="text-sm">Seguridad</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer px-3 py-2 focus:bg-[var(--blue-2)] focus:text-[var(--blue-11)] dark:focus:bg-[var(--blue-3)] dark:focus:text-[var(--blue-9)]"
                  onClick={() => setShowHelpModal(true)}
                >
                  <HelpCircle className="mr-3 h-4 w-4" />
                  <span className="text-sm">Ayuda y soporte</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-800" />
                <DropdownMenuItem
                  className="cursor-pointer px-3 py-2 text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-900/20 dark:focus:text-red-300"
                  onClick={onLogout}
                >
                  <LogOut className="mr-3 h-4 w-4" />
                  <span className="text-sm">Cerrar sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UserProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
      <SecuritySettingsModal
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
      />
      <HelpSupportModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </TooltipProvider>
  );
});
